'use client'
import Image from 'next/image'
import { trackEvent } from '@/lib/trackEvent'
import BetaBanner from '@/components/BetaBanner'

import { useState, useEffect, useRef } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useAuth } from '@/context/AuthContext'
import { useCurrency, setCurrencyPreference, SELECTOR_CURRENCIES } from '@/lib/currency'

const CURRENCY_FLAGS = { EUR: '🇪🇺', GBP: '🇬🇧', USD: '🇺🇸', CHF: '🇨🇭', SEK: '🇸🇪', NOK: '🇳🇴', DKK: '🇩🇰', TRY: '🇹🇷', PLN: '🇵🇱', RON: '🇷🇴', HUF: '🇭🇺', CZK: '🇨🇿' }

const NAV_LINKS = [
  { label: 'Deals', href: '/deals' },
  { label: 'Destinations', href: '/destinations' },
  { label: 'Airlines', href: '/airlines' },
  { label: 'Blog', href: '/blog' },
  { label: 'Stats', href: '/stats' },
  { label: 'FAQ', href: '/faq' },
  { label: 'About', href: '/about' },
]

const NAV_APP = [
  { href: '/app',              label: 'Dashboard'       },
  { href: '/app/deals',        label: 'My Deals'        },
  { href: '/app/destinations', label: 'My Destinations' },
  { href: '/app/airlines',     label: 'My Airlines'     },
  { href: '/app/alerts',       label: 'Alerts'          },
  { href: '/app/plan',         label: 'My Planning'     },
  { href: '/app/earn',         label: 'Share & Earn'    },
  { href: '/app/subscription', label: 'My Subscription' },
  { href: '/app/settings',     label: 'Settings'        },
]

const GearIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
  </svg>
)

const CloseIcon = () => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
)

const CrownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor">
    <path d="M2 19h20v2H2v-2zM22 5l-5 7-5-9-5 9-5-7v12h20V5z" />
  </svg>
)

function TierBadge({ tier = 'free' }) {
  const cls =
    tier === 'pro'  ? 'bg-amber-100 text-amber-700' :
    tier === 'plus' ? 'bg-blue-100 text-blue-700'   :
                      'bg-gray-100 text-gray-500'
  const label = tier.charAt(0).toUpperCase() + tier.slice(1)
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${cls}`} title={`${label} plan`}>
      {tier !== 'free' && <CrownIcon />}
      {label}
    </span>
  )
}

export default function Navbar({ currentUser: propUser, profile, onSignIn, onSignOut, onPreferences, onMenuOpen, appNav }) {
  const t = useTranslations('nav')
  const { currentUser: ctxUser, signOut: ctxSignOut, accessToken } = useAuth()
  const pathname = usePathname()
  const isBlog = pathname.startsWith('/blog')
  const router = useRouter()
  const [mobileOpen, setMobileOpen] = useState(false)
  const drawerRef = useRef(null)

  // Use explicit props when provided (DealsShell), otherwise fall back to context
  const currentUser = propUser !== undefined ? propUser : ctxUser
  const handleSignIn = onSignIn && pathname === '/' ? onSignIn : () => router.push('/signup')
  const handleSignOut = onSignOut ?? ctxSignOut

  // Fetch profile when not passed as prop (public site pages)
  const [fetchedProfile, setFetchedProfile] = useState(null)
  useEffect(() => {
    if (profile !== undefined || !currentUser || !accessToken) return
    fetch('/api/profile', { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(r => r.ok ? r.json() : null)
      .then(p => { if (p) setFetchedProfile(p) })
      .catch(() => {})
  }, [currentUser, accessToken, profile])
  const resolvedProfile = profile ?? fetchedProfile

  const tier = resolvedProfile?.tier ?? (resolvedProfile?.premium ? 'plus' : 'free')
  const upgradeLabel = tier === 'plus' ? 'Get Pro' : tier === 'free' ? 'Get Plus' : null
  const showUpgrade = currentUser && resolvedProfile != null && upgradeLabel !== null

  const { currency } = useCurrency()

  // Active link: exact match for '/', prefix match for others
  const isActive = (href) =>
    href === '/' ? pathname === '/' : pathname.startsWith(href)

  // Close on Escape
  useEffect(() => {
    if (!mobileOpen) return
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [mobileOpen])

  // Lock body scroll while drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [mobileOpen])

  const close = () => setMobileOpen(false)

  return (
    <>
      <nav className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between">

          {/* Logo + Site/App tabs */}
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2 text-lg font-bold tracking-tight text-blue-600">
              <Image src="/logo-512.png" alt="" width={28} height={28} className="rounded-md shrink-0" />
              FairFares
            </a>
          </div>

          
          
            <div className="hidden md:flex items-center gap-6">
              {NAV_LINKS.map(({ label, href }) => (
                <a key={label} href={href}
                  className={`text-sm font-medium transition-colors ${
                    isActive(href)
                      ? 'text-blue-600'
                      : 'text-gray-600 hover:text-blue-600'
                  }`}
                  aria-current={isActive(href) ? 'page' : undefined}
                >
                  {label}
                </a>
              ))}
            </div>


          {/* Desktop — right controls */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href="https://x.com/FairFaresApp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow FairFares on X"
              className="text-gray-500 hover:text-gray-700 transition-colors p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.261 5.632 5.903-5.632zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href="https://t.me/fairfares"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Join FairFares on Telegram"
              className="text-gray-500 hover:text-gray-700 transition-colors p-2 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
              </svg>
            </a>
            <select
              value={currency}
              onChange={(e) => setCurrencyPreference(e.target.value)}
              className="text-xs rounded-lg border border-gray-200 bg-white px-2 py-1 text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer"
              aria-label="Display currency"
            >
              {SELECTOR_CURRENCIES.map((c) => (
                <option key={c} value={c}>{CURRENCY_FLAGS[c]} {c}</option>
              ))}
            </select>
            {currentUser ? (
              <div className="flex items-center gap-2.5">
                {showUpgrade && (
                  <a href="/pricing"
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors">
                    {upgradeLabel}
                  </a>
                )}
                <span className="flex items-center gap-1.5">
                  <span className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-bold">
                    {(currentUser.email?.[0] ?? '?').toUpperCase()}
                  </span>
                  <TierBadge tier={resolvedProfile?.tier ?? 'free'} />
                </span>
                <button onClick={handleSignOut}
                  className="text-xs text-gray-600 hover:text-gray-900 transition-colors">
                  {t('signOut')}
                </button>
              </div>
            ) : onSignIn ? (
              <button onClick={handleSignIn}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-4 py-1.5 transition-colors">
                Sign up free
              </button>
            ) : (
              <a href="/signin" onClick={() => trackEvent('signup_cta', { label: 'Sign up free', component: 'Navbar' }, 'guest')}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-4 py-1.5 transition-colors inline-block">
                Sign up free
              </a>
            )}
          </div>

          {/* Mobile — hamburger */}
          <button
            className="md:hidden p-2 -mr-1 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
            onClick={() => onMenuOpen ? onMenuOpen() : setMobileOpen((o) => !o)}
            aria-label="Open menu"
            aria-expanded={onMenuOpen ? undefined : mobileOpen}
            aria-controls={onMenuOpen ? undefined : 'mobile-drawer'}
          >
            {mobileOpen ? <CloseIcon /> : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile drawer overlay */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40" aria-hidden="true" onClick={close} />

            {/* Slide-out panel */}
            <div
              ref={drawerRef}
              id="mobile-drawer"
              className="absolute top-0 right-0 h-full w-72 bg-white shadow-xl flex flex-col"
            >
              {/* Drawer header */}
              <div className="px-4 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-2 text-lg font-bold text-blue-600">
                    <Image src="/logo-512.png" alt="" width={28} height={28} className="rounded-md shrink-0" aria-hidden="true" />
                    FairFares
                  </span>
                </div>
                <button onClick={close}
                  className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
                  aria-label="Close menu">
                  <CloseIcon />
                </button>
              </div>

              {/* Drawer nav links */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {(() => {
                  const navItems = appNav ?? (currentUser ? NAV_APP : null)
                  return navItems ? (
                    <>
                      <ul className="space-y-0.5 mb-3">
                        {navItems.map(({ href, label, icon }) => (
                          <li key={href}>
                            <a href={href} onClick={close}
                              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
                                isActive(href) ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }`}>
                              {icon && <span className={isActive(href) ? 'text-blue-600' : 'text-gray-500'}>{icon}</span>}
                              {label}
                            </a>
                          </li>
                        ))}
                      </ul>
                      <div className="border-t border-gray-100 mb-3" />
                    </>
                  ) : null
                })()}
                <ul className="space-y-1">
                  {NAV_LINKS.map(({ label, href }) => (
                    <li key={label}>
                      <a href={href} onClick={close}
                        aria-current={isActive(href) ? 'page' : undefined}
                        className={`flex items-center rounded-lg px-3 py-3 text-base font-medium transition-colors ${
                          isActive(href)
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                        }`}>
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>

                {/* Currency selector in drawer */}
                <div className="mt-4 pt-4 border-t border-gray-100 px-3">
                  <p className="mb-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Currency</p>
                  <select
                    value={currency}
                    onChange={(e) => setCurrencyPreference(e.target.value)}
                    className="w-full text-sm rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
                    aria-label="Display currency"
                  >
                    {SELECTOR_CURRENCIES.map((c) => (
                      <option key={c} value={c}>{CURRENCY_FLAGS[c]} {c}</option>
                    ))}
                  </select>
                </div>

                {/* Social links in drawer */}
                <div className="mt-4 pt-4 border-t border-gray-100 px-3 space-y-3">
                  <a
                    href="https://x.com/FairFaresApp"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.261 5.632 5.903-5.632zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    Follow us on X
                  </a>
                  <a
                    href="https://t.me/fairfares"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={close}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
                  >
                    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                    </svg>
                    Join us on Telegram
                  </a>
                </div>
              </div>

              {/* Drawer footer — auth */}
              <div className="px-4 py-4 border-t border-gray-100 shrink-0">
                {currentUser ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
                      <TierBadge tier={resolvedProfile?.tier ?? 'free'} />
                    </div>
                    {showUpgrade && (
                      <a
                        href="/pricing"
                        onClick={close}
                        className="w-full inline-block text-center rounded-lg bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
                      >
                        {upgradeLabel}
                      </a>
                    )}
                    {onPreferences && (
                      <button
                        onClick={() => { close(); onPreferences() }}
                        className="w-full flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-gray-500"><GearIcon /></span>
                        {t('preferences')}
                        {profile?.emailAlerts && (
                          <span className="ml-auto h-2 w-2 rounded-full bg-blue-500 shrink-0" aria-hidden="true" />
                        )}
                      </button>
                    )}
                    <button
                      onClick={() => { close(); handleSignOut() }}
                      className="w-full text-left rounded-lg px-3 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
                    >
                      {t('signOut')}
                    </button>
                  </div>
                ) : onSignIn ? (
                  <button
                    onClick={() => { close(); handleSignIn() }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg px-4 py-3 transition-colors"
                  >
                    Sign up free
                  </button>
                ) : (
                  <a
                    href="/signin" onClick={() => trackEvent('signup_cta', { label: 'Sign up free', component: 'Navbar' }, 'guest')}
                    onClick={close}
                    className="w-full inline-block text-center bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg px-4 py-3 transition-colors"
                  >
                    Sign up free
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
      {process.env.NEXT_PUBLIC_BETA_BANNER === 'true' && <BetaBanner />}
    </>
  )
}
